import { useGetOrganizationQuery } from "modules/auth/hooks/useGetOrganizationQuery"
import { useIntegrationsStatusQuery } from "modules/settings/hooks/useIntegrationsStatusQuery"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  CapabilitiesResponseDataSchema,
  CompanyIntegrationProviderEnum,
  GetAllIntegrationsStatusResponseData,
} from "schema"
import z from "zod"
import { getProviderOptionKey, isProviderEnabled } from "../lib/providerUtils"

type IntegrationStatus = z.infer<typeof GetAllIntegrationsStatusResponseData>
type Provider = z.infer<typeof CompanyIntegrationProviderEnum>

type UseCapabilityProviderOptions = {
  capability: z.infer<typeof CapabilitiesResponseDataSchema>[number]
  mode?: "install" | "update"
  initialConfig?: { provider?: string; organizationPhoneNumbers?: string | null }
}

type ProviderOption = {
  value: string
  label: string
  disabled: boolean
}

export function useCapabilityProvider({
  capability,
  mode = "install",
  initialConfig,
}: UseCapabilityProviderOptions) {
  const { t } = useTranslation()
  const supportedProviders = useMemo(
    () => capability.supportedProviders ?? [],
    [capability.supportedProviders],
  )
  const defaultProvider = supportedProviders[0] ?? null

  const [provider, setProvider] = useState<string | null>(
    (initialConfig?.provider as string) ?? defaultProvider,
  )
  const [organizationPhoneNumbers, setOrganizationPhoneNumbers] = useState<string | null>(
    initialConfig?.organizationPhoneNumbers ?? "",
  )

  const { data: integrationsStatus, isPending: isIntegrationsStatusPending } =
    useIntegrationsStatusQuery({
      onSuccess: (data) => {
        if (mode === "install" && !initialConfig?.provider) {
          const firstAvailableProvider =
            supportedProviders.find((p) =>
              isProviderEnabled(p as Provider, data as IntegrationStatus),
            ) ?? null
          setProvider(firstAvailableProvider)
        }
      },
    })

  const { data: organizationData, isPending: isOrganizationPending } = useGetOrganizationQuery()

  useEffect(() => {
    if (!isOrganizationPending && organizationData) {
      setOrganizationPhoneNumbers(organizationData.phoneNumbers ?? "")
    }
  }, [organizationData, isOrganizationPending])

  useEffect(() => {
    if (initialConfig?.provider && mode === "update") {
      setProvider(initialConfig.provider as string)
    } else if (!initialConfig?.provider && mode === "install") {
      setProvider(defaultProvider)
    }
  }, [initialConfig, mode, defaultProvider])

  const providerOptions = useMemo<ProviderOption[]>(() => {
    return supportedProviders.map((providerValue) => {
      const isEnabled = isProviderEnabled(providerValue as Provider, integrationsStatus)
      const optionKey = getProviderOptionKey(providerValue as Provider)

      return {
        value: providerValue,
        label: t(`capabilities.catalog.${capability.identifier}.options.${optionKey}`, {
          defaultValue: providerValue,
        }),
        disabled: !isEnabled,
      }
    })
  }, [supportedProviders, integrationsStatus, capability.identifier, t])

  const handleProviderChange = (value: string | undefined) => {
    setProvider(value ?? null)
  }

  return {
    provider,
    setProvider: handleProviderChange,
    providerOptions,
    isIntegrationsStatusPending,
    organizationPhoneNumbers,
    setOrganizationPhoneNumbers,
  }
}
